import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import { PlusOutlined } from '@ant-design/icons';
import {
  Alert, Button, Input, Select,
} from 'antd';
import DatePicker from '../DatePicker';
import EhrField from '../ehr/EhrField';
import { hasPresentValue, parsePrefillDate } from '../../util/ehr';
import './nextgenInsuranceSection.scss';

// Create Chart insurance for NextGen (ENG-3967). Staff pick each payer from the
// selected practice's NextGen payers; the picked payerId is what the chart
// upload sends (insurance_details.coverages[].package_id), primary as
// coordination-of-benefits 1 and secondary as 2.

const trimmed = (value) => String(value ?? '').trim();
const getDrawerPopupContainer = () => document.body;

// One payer request per credential and practice at a time. A cold load reads
// the practice's whole NextGen payer list, so a remount, or closing and
// reopening the section while it runs, joins that load instead of starting
// another. Settled requests are dropped; the server caches the list.
const pendingPayerRequests = new Map();
const loadPayers = (credentialId, practice) => {
  const key = `${credentialId}:${practice}`;
  if (!pendingPayerRequests.has(key)) {
    const request = axios.get('/api/v1/emr_options', {
      params: { emr_integration_credential_id: credentialId, source: 'nextgen/payers', location: practice },
    }).then((response) => response.data.options || [])
      .finally(() => pendingPayerRequests.delete(key));
    pendingPayerRequests.set(key, request);
  }
  return pendingPayerRequests.get(key);
};

// Mirrors NextgenInsuranceSync#normalize_payer_name: lowercase, anything but
// letters and digits becomes a space, spaces squeezed.
export const normalizePayerName = (name) => trimmed(name).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// An option's label adds the payer's financial class and address; the bare
// NextGen name and those details ride in metadata.
const payerName = (payer) => payer.metadata?.name ?? payer.label;

// Two-line option, as in the referring provider search: the payer's name, then
// its financial class and address, so a long address wraps instead of being cut.
export const PayerOptionContent = ({ payer }) => (
  <div className="nextgen-insurance-section__payer-option">
    <span>{payerName(payer)}</span>
    {payer.metadata?.details && <span className="nextgen-insurance-section__payer-details">{payer.metadata.details}</span>}
  </div>
);
PayerOptionContent.propTypes = {
  payer: PropTypes.shape({
    label: PropTypes.string,
    metadata: PropTypes.shape({ name: PropTypes.string, details: PropTypes.string }),
  }).isRequired,
};

// The payer extracted from the document is pre-selected only when its name
// matches exactly one NextGen payer; anything looser could attach the wrong one.
export const uniquePayerMatch = (payers, name) => {
  const target = normalizePayerName(name);
  if (!target) return null;
  const matches = payers.filter((payer) => normalizePayerName(payerName(payer)) === target);
  return matches.length === 1 ? matches[0] : null;
};

const coverageValidationError = (payer, values) => {
  if (!payer) return 'Select a payer.';
  if (!hasPresentValue(values.memberId)) return 'Member ID is required.';
  if (!values.effectiveDate) return 'Effective date is required.';
  return null;
};

const NextgenInsuranceCoverage = ({
  initialPrefill, loadingPayers, onAnalytics, onChange, onValidationChange, payers, payersReady, sequenceNumber, title,
}) => {
  const [payer, setPayer] = useState(null);
  const [suggested, setSuggested] = useState(false);
  const [values, setValues] = useState(() => ({
    memberId: trimmed(initialPrefill.memberId),
    groupNumber: trimmed(initialPrefill.groupNumber),
    effectiveDate: parsePrefillDate(initialPrefill.effectiveDate) || moment().startOf('day'),
  }));
  const payerRef = useRef(null);
  const touchedRef = useRef(false);
  // Declared before the payer-list effect below so it sees the committed payer.
  useEffect(() => { payerRef.current = payer; }, [payer]);
  const updateValues = useCallback((updates) => setValues((current) => ({ ...current, ...updates })), []);

  // A new payer list (first load or another practice) keeps the selection only
  // if this practice offers it. Until staff choose a payer themselves, the
  // document's payer is suggested on a unique exact match.
  useEffect(() => {
    if (!payersReady) return;
    const { current } = payerRef;
    if (current && payers.some((option) => option.value === current.value)) return;
    const match = touchedRef.current ? null : uniquePayerMatch(payers, initialPrefill.payerName);
    setPayer(match);
    setSuggested(Boolean(match));
  }, [initialPrefill.payerName, payers, payersReady]);

  const validationError = useMemo(() => coverageValidationError(payer, values), [payer, values]);
  const coverage = useMemo(() => {
    if (validationError) return null;
    return {
      sequence_number: sequenceNumber,
      package_id: payer.value,
      package_name: payerName(payer),
      member_id: trimmed(values.memberId),
      group_number: trimmed(values.groupNumber) || undefined,
      effective_date: values.effectiveDate.format('YYYY-MM-DD'),
    };
  }, [payer, sequenceNumber, validationError, values]);
  useEffect(() => { onValidationChange(validationError); onChange(coverage); }, [coverage, onChange, onValidationChange, validationError]);

  const choosePayer = (payerId) => {
    const next = payers.find((option) => option.value === payerId) || null;
    const replacedSuggestion = suggested;
    touchedRef.current = true;
    setPayer(next);
    setSuggested(false);
    if (next) onAnalytics?.('nextgen_insurance_payer_selected', { coverage: sequenceNumber, replaced_suggestion: replacedSuggestion });
    else onAnalytics?.('nextgen_insurance_payer_cleared', { coverage: sequenceNumber, replaced_suggestion: replacedSuggestion });
  };

  const documentPayer = trimmed(initialPrefill.payerName);
  const idPrefix = `nextgen-insurance-${sequenceNumber}`;
  return (
    <section className="nextgen-insurance-section__coverage" aria-label={title}>
      <EhrField controlId={`${idPrefix}-payer`} label="Payer" required>
        <Select
          id={`${idPrefix}-payer`}
          aria-required="true"
          showSearch
          allowClear
          value={payer?.value}
          loading={loadingPayers}
          disabled={!payersReady}
          placeholder={loadingPayers ? 'Loading payers...' : 'Search NextGen payers'}
          filterOption={(input, option) => normalizePayerName(option.title).includes(normalizePayerName(input))}
          optionLabelProp="label"
          notFoundContent="No matching payer"
          onChange={choosePayer}
          getPopupContainer={getDrawerPopupContainer}
          dropdownClassName="ehr-select-dropdown"
        >
          {payers.map((option) => (
            <Select.Option key={option.value} value={option.value} label={option.label} title={option.label}>
              <PayerOptionContent payer={option} />
            </Select.Option>
          ))}
        </Select>
      </EhrField>
      {payer && suggested && <p className="nextgen-insurance-section__hint">Matched to the payer on the document. Confirm or change it.</p>}
      {!payer && payersReady && documentPayer && (
        <p className="nextgen-insurance-section__hint">
          {`The document lists "${documentPayer}", which doesn't match exactly one NextGen payer. Select the payer.`}
        </p>
      )}
      <div className="nextgen-insurance-section__grid">
        <EhrField controlId={`${idPrefix}-member`} label="Member ID" required><Input id={`${idPrefix}-member`} aria-required="true" value={values.memberId} onChange={(event) => updateValues({ memberId: event.target.value })} /></EhrField>
        <EhrField controlId={`${idPrefix}-group`} label="Group number"><Input id={`${idPrefix}-group`} value={values.groupNumber} onChange={(event) => updateValues({ groupNumber: event.target.value })} /></EhrField>
        <EhrField controlId={`${idPrefix}-effective`} label="Effective date" required><DatePicker id={`${idPrefix}-effective`} aria-required="true" value={values.effectiveDate} onChange={(value) => updateValues({ effectiveDate: value })} getPopupContainer={getDrawerPopupContainer} dropdownClassName="ehr-select-dropdown" /></EhrField>
      </div>
    </section>
  );
};
NextgenInsuranceCoverage.propTypes = {
  initialPrefill: PropTypes.object.isRequired, loadingPayers: PropTypes.bool.isRequired, onAnalytics: PropTypes.func, onChange: PropTypes.func.isRequired, onValidationChange: PropTypes.func.isRequired, payers: PropTypes.array.isRequired, payersReady: PropTypes.bool.isRequired, sequenceNumber: PropTypes.number.isRequired, title: PropTypes.string.isRequired,
};
NextgenInsuranceCoverage.defaultProps = { onAnalytics: null };

const NextgenInsuranceSection = ({
  credentialId, practice, onAnalytics, onChange, onValidationChange, prefill,
}) => {
  const initiallyExpanded = hasPresentValue(prefill.primary_plan) || hasPresentValue(prefill.primary_member_id) || hasPresentValue(prefill.secondary_plan) || hasPresentValue(prefill.secondary_member_id);
  const initiallySecondary = hasPresentValue(prefill.secondary_plan) || hasPresentValue(prefill.secondary_member_id);
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [secondaryExpanded, setSecondaryExpanded] = useState(initiallySecondary);
  const [payersState, setPayersState] = useState({
    practice: null, payers: [], loading: false, error: null,
  });
  const [primaryCoverage, setPrimaryCoverage] = useState(null); const [secondaryCoverage, setSecondaryCoverage] = useState(null);
  const [primaryValidationError, setPrimaryValidationError] = useState(initiallyExpanded ? 'Select a payer.' : null);
  const [secondaryValidationError, setSecondaryValidationError] = useState(initiallySecondary ? 'Select a payer.' : null);
  const onChangeRef = useRef(onChange);
  const onValidationChangeRef = useRef(onValidationChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onValidationChangeRef.current = onValidationChange; }, [onValidationChange]);
  // Payers are per practice: NextGen refuses an insurance whose payer the
  // practice cannot use, so the list reloads whenever the practice changes.
  useEffect(() => {
    if (!expanded || !credentialId || !hasPresentValue(practice)) {
      setPayersState({
        practice: null, payers: [], loading: false, error: null,
      });
      return undefined;
    }
    // Only this run's response may land: a newer practice, or an unmount, ends
    // its ownership. The shared request itself is not cancelled.
    let active = true;
    setPayersState({
      practice, payers: [], loading: true, error: null,
    });
    loadPayers(credentialId, practice).then((payers) => {
      if (!active) return;
      setPayersState({
        practice, payers, loading: false, error: null,
      });
    }).catch(() => {
      if (!active) return;
      setPayersState({
        practice, payers: [], loading: false, error: 'Unable to load NextGen payers for this practice. Try again, or remove insurance to create the chart without it.',
      });
      onAnalytics?.('nextgen_insurance_payers_load_failed', {});
    });
    return () => { active = false; };
  }, [credentialId, expanded, onAnalytics, practice]);

  const payersReady = !payersState.loading && !payersState.error && payersState.practice === practice && hasPresentValue(practice);
  const samePayer = secondaryExpanded && primaryCoverage && secondaryCoverage && primaryCoverage.package_id === secondaryCoverage.package_id;
  let validationError = null;
  if (expanded) {
    if (!hasPresentValue(practice)) validationError = 'Select a practice to choose insurance payers.';
    else if (primaryValidationError) validationError = `Primary insurance: ${primaryValidationError}`;
    else if (secondaryExpanded && secondaryValidationError) validationError = `Secondary insurance: ${secondaryValidationError}`;
    else if (samePayer) validationError = 'Secondary insurance: choose a different payer than the primary.';
  }
  useEffect(() => {
    onValidationChangeRef.current?.(validationError);
    if (!expanded || validationError) {
      onChangeRef.current?.(undefined);
      return;
    }
    onChangeRef.current?.({ coverages: [primaryCoverage, ...(secondaryExpanded ? [secondaryCoverage] : [])] });
  }, [expanded, primaryCoverage, secondaryCoverage, secondaryExpanded, validationError]);

  const add = () => { setExpanded(true); setPrimaryValidationError('Select a payer.'); onAnalytics?.('nextgen_insurance_expanded', { source: 'manual' }); };
  const remove = () => { setExpanded(false); setSecondaryExpanded(false); setPrimaryCoverage(null); setSecondaryCoverage(null); setPrimaryValidationError(null); setSecondaryValidationError(null); onAnalytics?.('nextgen_insurance_removed', {}); };
  const addSecondary = () => { setSecondaryExpanded(true); setSecondaryValidationError('Select a payer.'); onAnalytics?.('nextgen_secondary_insurance_added', {}); };
  const removeSecondary = () => { setSecondaryExpanded(false); setSecondaryCoverage(null); setSecondaryValidationError(null); onAnalytics?.('nextgen_secondary_insurance_removed', {}); };

  if (!expanded) {
    return (
      <div className="nextgen-insurance-section">
        <p className="nextgen-insurance-section__intro">Optional: add primary and secondary insurance to the new patient chart.</p>
        <Button className="nextgen-insurance-section__add-button" icon={<PlusOutlined />} onClick={add}>Add insurance</Button>
      </div>
    );
  }
  return (
    <div className="nextgen-insurance-section" data-testid="nextgen-insurance-section">
      <div className="nextgen-insurance-section__header">
        <strong>Insurance</strong>
        <Button type="link" onClick={remove}>Remove</Button>
      </div>
      <p className="nextgen-insurance-section__help">Choose each payer from this practice&apos;s NextGen payers. The effective date defaults to today.</p>
      {payersState.error && <Alert type="warning" showIcon message={payersState.error} />}
      {validationError && <Alert type="error" showIcon message={validationError} />}
      <h3 className="nextgen-insurance-section__coverage-title">Primary insurance</h3>
      <NextgenInsuranceCoverage
        loadingPayers={payersState.loading}
        onAnalytics={onAnalytics}
        payers={payersState.payers}
        payersReady={payersReady}
        initialPrefill={{
          payerName: prefill.primary_plan, memberId: prefill.primary_member_id, groupNumber: prefill.insurance_group_number, effectiveDate: prefill.insurance_effective_date,
        }}
        onChange={setPrimaryCoverage}
        onValidationChange={setPrimaryValidationError}
        sequenceNumber={1}
        title="Primary insurance"
      />
      {secondaryExpanded ? (
        <>
          <div className="nextgen-insurance-section__secondary-header">
            <h3 className="nextgen-insurance-section__coverage-title">Secondary insurance</h3>
            <Button type="link" onClick={removeSecondary}>Remove secondary</Button>
          </div>
          <NextgenInsuranceCoverage
            loadingPayers={payersState.loading}
            onAnalytics={onAnalytics}
            payers={payersState.payers}
            payersReady={payersReady}
            initialPrefill={{ payerName: prefill.secondary_plan, memberId: prefill.secondary_member_id, groupNumber: prefill.secondary_group_number }}
            onChange={setSecondaryCoverage}
            onValidationChange={setSecondaryValidationError}
            sequenceNumber={2}
            title="Secondary insurance"
          />
        </>
      ) : <Button className="nextgen-insurance-section__add-button" icon={<PlusOutlined />} onClick={addSecondary}>Add secondary insurance</Button>}
    </div>
  );
};
NextgenInsuranceSection.propTypes = {
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), practice: PropTypes.string, onAnalytics: PropTypes.func, onChange: PropTypes.func, onValidationChange: PropTypes.func, prefill: PropTypes.object,
};
NextgenInsuranceSection.defaultProps = {
  credentialId: null, practice: null, onAnalytics: null, onChange: null, onValidationChange: null, prefill: {},
};
export default NextgenInsuranceSection;
